import { BINARY_KIND } from '@glyph/protocol-ts';
import type { DeviceId, Topic } from '@glyph/protocol-ts';
import type { BinaryEvent, ClientEvent, EventHandler, Unsubscribe } from './types';

export interface MuxSocketOptions {
  url: () => string;
  /** Build the subscribe/unsubscribe frames for this transport. */
  subFrame: (deviceId: DeviceId, topics: Topic[]) => unknown;
  unsubFrame: (deviceId: DeviceId, topics: Topic[]) => unknown;
  /** Map an incoming JSON frame to a ClientEvent (or null to ignore). */
  parse: (msg: any) => ClientEvent | ClientEvent[] | null;
  /** Binary frames: the first byte is the kind; the device id for single-device sockets is fixed. */
  binaryDeviceId?: (frame: DataView) => DeviceId;
  pingIntervalMs?: number;
  /** Keepalive frame (default `{t:'ping'}`, the agent hub's); the cloud gateway wants `{op:'ping'}`. */
  pingFrame?: unknown;
  /** Binary frames carry a raw 16-byte device uuid before the inner frame (cloud gateway). */
  binaryPrefixBytes?: number;
  WebSocketImpl?: typeof WebSocket;
}

/**
 * One multiplexed WebSocket shared by every hook. Tracks per-(device, topic) refcounts so the first
 * subscriber sends the subscribe frame and the last one sends unsubscribe; reconnects with backoff
 * 0.5 s -> 8 s and re-subscribes everything on open.
 */
export class MuxSocket {
  private ws: WebSocket | null = null;
  private handlers = new Set<EventHandler>();
  private refs = new Map<string, number>(); // `${deviceId}|${topic}` -> count
  private backoff = 500;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private ping: ReturnType<typeof setInterval> | null = null;
  private closed = false;
  connected = false;

  constructor(private readonly o: MuxSocketOptions) {}

  private key(d: DeviceId, t: Topic) {
    return `${d}|${t}`;
  }

  subscribe(deviceId: DeviceId, topics: readonly Topic[], onEvent: EventHandler): Unsubscribe {
    // Deliver only what this subscription asked for. Every handler used to see every frame, so a
    // status query that refetches on "health" also refetched on each 16 Hz level frame and each
    // 50 Hz audio frame (bench: ~35 status.get/s idle, ~140/s while listening, starving the UI).
    const wanted = new Set<string>(topics);
    const binaryKinds = new Set<string>();
    if (wanted.has('audio_opus')) binaryKinds.add('audio');
    if (wanted.has('preview')) binaryKinds.add('preview');
    const handler: EventHandler = (ev) => {
      if (ev.topic === '$connection') return onEvent(ev);
      if (ev.deviceId !== deviceId && ev.deviceId !== '*') return;
      if (ev.topic === '$binary') {
        if (binaryKinds.has(ev.data.kind)) onEvent(ev);
        return;
      }
      if (wanted.has(ev.topic)) onEvent(ev);
    };
    this.handlers.add(handler);
    const fresh: Topic[] = [];
    for (const t of topics) {
      const k = this.key(deviceId, t);
      const n = (this.refs.get(k) ?? 0) + 1;
      this.refs.set(k, n);
      if (n === 1) fresh.push(t);
    }
    if (fresh.length) this.send(this.o.subFrame(deviceId, fresh));
    this.ensureOpen();
    return () => {
      this.handlers.delete(handler);
      const gone: Topic[] = [];
      for (const t of topics) {
        const k = this.key(deviceId, t);
        const n = (this.refs.get(k) ?? 1) - 1;
        if (n <= 0) {
          this.refs.delete(k);
          gone.push(t);
        } else this.refs.set(k, n);
      }
      if (gone.length) this.send(this.o.unsubFrame(deviceId, gone));
    };
  }

  send(frame: unknown) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(frame));
  }

  /** Topics still subscribed for a device (after the unsubscribe bookkeeping ran). */
  topicsFor(deviceId: DeviceId): Topic[] {
    const prefix = `${deviceId}|`;
    return [...this.refs.keys()].filter((k) => k.startsWith(prefix)).map((k) => k.slice(prefix.length) as Topic);
  }

  private emit(ev: ClientEvent) {
    for (const h of this.handlers) {
      try {
        h(ev);
      } catch (e) {
        console.error('[ws] handler failed', e);
      }
    }
  }

  ensureOpen() {
    if (this.closed || this.ws) return;
    const WS = this.o.WebSocketImpl ?? (typeof WebSocket !== 'undefined' ? WebSocket : null);
    if (!WS) return;
    const ws = new WS(this.o.url());
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    ws.onopen = () => {
      this.connected = true;
      this.backoff = 500;
      // Re-subscribe everything, grouped by device.
      const byDev = new Map<DeviceId, Topic[]>();
      for (const k of this.refs.keys()) {
        const [d, t] = k.split('|') as [DeviceId, Topic];
        byDev.set(d, [...(byDev.get(d) ?? []), t]);
      }
      for (const [d, ts] of byDev) this.send(this.o.subFrame(d, ts));
      this.emit({ topic: '$connection', deviceId: '*', ts: Date.now(), data: { connected: true } });
      if (this.o.pingIntervalMs)
        this.ping = setInterval(() => this.send(this.o.pingFrame ?? { t: 'ping' }), this.o.pingIntervalMs);
    };
    ws.onmessage = (m) => {
      if (typeof m.data === 'string') {
        let msg: any;
        try {
          msg = JSON.parse(m.data);
        } catch {
          return;
        }
        const ev = this.o.parse(msg);
        if (Array.isArray(ev)) for (const e of ev) this.emit(e);
        else if (ev) this.emit(ev);
      } else if (m.data instanceof ArrayBuffer) {
        const bin = parseBinary(m.data, this.o.binaryDeviceId, this.o.binaryPrefixBytes);
        if (bin) this.emit({ topic: '$binary', deviceId: bin.deviceId, ts: Date.now(), data: bin });
      }
    };
    ws.onclose = () => {
      this.connected = false;
      this.ws = null;
      if (this.ping) clearInterval(this.ping);
      this.ping = null;
      this.emit({ topic: '$connection', deviceId: '*', ts: Date.now(), data: { connected: false } });
      if (!this.closed && this.refs.size) {
        this.timer = setTimeout(() => this.ensureOpen(), this.backoff);
        this.backoff = Math.min(8000, this.backoff * 2);
      }
    };
    ws.onerror = () => ws.close();
  }

  /**
   * Drop the socket and open a fresh one (same subscriptions): the URL is re-evaluated, which is
   * how a session token obtained after the page loaded (PIN unlock) reaches the server. Topics
   * the server refused on the unauthenticated socket (audio, logs, ...) are subscribed again.
   */
  reconnect() {
    if (this.closed) return;
    const ws = this.ws;
    this.ws = null;
    this.connected = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (this.ping) clearInterval(this.ping);
    this.ping = null;
    if (ws) {
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.close();
    }
    if (this.refs.size) this.ensureOpen();
  }

  close() {
    this.closed = true;
    if (this.timer) clearTimeout(this.timer);
    if (this.ping) clearInterval(this.ping);
    this.ws?.close();
    this.ws = null;
  }
}

/**
 * Binary frame layouts:
 *  audio:   [0x01][u16 seq][u32 ts_ms][opus...]            (device -> cloud/browser, PLAN "Live audio listen")
 *  preview: [0x02][u8 output][jpeg...]                     (local /ws preview topic)
 *  cloud wraps both with a 16-byte device id prefix: [0x10][16 bytes ulid/uuid bin][inner frame]
 */
export function parseBinary(buf: ArrayBuffer, deviceIdOf?: (dv: DataView) => DeviceId, prefixBytes = 0): BinaryEvent | null {
  let dv = new DataView(buf);
  let deviceId: DeviceId = 'self';
  if (prefixBytes === 16) {
    // cloud gateway: raw uuid bytes, no marker byte
    if (buf.byteLength < 17) return null;
    deviceId = uuidFromBytes(new Uint8Array(buf, 0, 16));
    dv = new DataView(buf, 16);
  } else if (dv.getUint8(0) === 0x10) {
    deviceId = uuidFromBytes(new Uint8Array(buf, 1, 16));
    dv = new DataView(buf, 17);
  } else if (deviceIdOf) deviceId = deviceIdOf(dv);
  const kind = dv.getUint8(0);
  if (kind === BINARY_KIND.AUDIO_OPUS) {
    const seq = dv.getUint16(1);
    const tsMs = dv.getUint32(3);
    return { kind: 'audio', deviceId, seq, tsMs, opus: new Uint8Array(dv.buffer, dv.byteOffset + 7) };
  }
  if (kind === BINARY_KIND.PREVIEW_JPEG) {
    return { kind: 'preview', deviceId, jpeg: new Uint8Array(dv.buffer, dv.byteOffset + 2), ts: Date.now() };
  }
  return null;
}

function uuidFromBytes(b: Uint8Array): string {
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
